/**
 * Command palette registry (BUILD-PLAN 3.6). One registry feeds the palette,
 * and commands with a shortcut also appear in the `?` sheet.
 *
 * Modules add commands in two ways:
 * - declaratively, in their manifest (`commands` with a `routeId`): the shell
 *   registers a "go to" command for each;
 * - at runtime, with `registerCommand()` or the `useRegisterCommand()` hook,
 *   for verbs that need code ("Text patient...", "Run eligibility...").
 *
 * Search providers add live result groups (patients, appointments, codes).
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { createRegistry } from '../registry/store.js';
import type { OfficeTaggedHit } from '../office/PatientOfficeTag.js';

export type CommandGroup = 'Navigate' | 'Actions' | 'Offices' | 'Preferences' | 'Help' | string;

export interface ShellCommand {
  /** Unique, `<moduleId>.<name>`. Re-registering an id replaces it. */
  id: string;
  title: string;
  group: CommandGroup;
  keywords?: readonly string[];
  /** Muted text after the title. */
  hint?: string;
  /** Lucide icon name (see app/icons.tsx). */
  icon?: string;
  /** Shortcut in manifest notation (`g s`, `mod+k`); shown, not bound (see useShortcut). */
  shortcut?: string;
  /** Permission the user needs in the current office scope; hidden otherwise. */
  permission?: string;
  /** Static ranking weight (default 0). */
  weight?: number;
  run: () => void;
}

export const commandRegistry = createRegistry<ShellCommand>();

/** Adds a command; returns a function that removes it. */
export function registerCommand(command: ShellCommand): () => void {
  return commandRegistry.register(command);
}

/**
 * Registers a command for the lifetime of the calling component. The latest
 * `run` is always used, so callers need not memoise it.
 */
export function useRegisterCommand(command: ShellCommand | null): void {
  const runRef = useRef(command?.run);
  runRef.current = command?.run;
  const key = command
    ? JSON.stringify([
        command.id,
        command.title,
        command.group,
        command.keywords,
        command.hint,
        command.icon,
        command.shortcut,
        command.permission,
        command.weight,
      ])
    : null;
  useEffect(() => {
    if (!command || key === null) return undefined;
    return registerCommand({ ...command, run: () => runRef.current?.() });
    // `key` captures every field but `run`, which is read through the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/* ---------- search providers ---------- */

export interface SearchResult {
  id: string;
  label: string;
  hint?: string;
  icon?: string;
  keywords?: readonly string[];
  /** True when the label or hint is PHI: masked in privacy mode. */
  phi?: boolean;
  /**
   * A patient's offices: in All offices mode the palette tags the row with
   * the office code (`PatientOfficeTag`). Omit for anything that is not a patient.
   */
  office?: OfficeTaggedHit;
  /** A small trailing mark the providing module renders (the patient grade badge). */
  adornment?: ReactNode;
  run: () => void;
}

export interface SearchContext {
  signal: AbortSignal;
  /** Office selector value (`all` or an office id). */
  office: string;
}

export interface SearchProvider {
  /** Re-registering an id replaces the provider (WP1-PATIENTS replaces `patients`). */
  id: string;
  heading: string;
  /** Minimum query length before `search` is called (default 2). */
  minLength?: number;
  /** Lower first; patients lead (BUILD-PLAN 3.6). */
  order?: number;
  /** Only shown in the palette's patient mode (`/`) when true or omitted. */
  patients?: boolean;
  permission?: string;
  search(query: string, ctx: SearchContext): Promise<readonly SearchResult[]>;
}

export const searchRegistry = createRegistry<SearchProvider>();

export function registerSearchProvider(provider: SearchProvider): () => void {
  return searchRegistry.register(provider);
}
